import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import MarketingHome from "@/pages/marketing/MarketingHome";
import MarketingServices from "@/pages/marketing/MarketingServices";
import MarketingSuccess from "@/pages/marketing/MarketingSuccess";
import MarketingAbout from "@/pages/marketing/MarketingAbout";
import MarketingContact from "@/pages/marketing/MarketingContact";
import MarketingRegister from "@/pages/marketing/MarketingRegister";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Quizzes from "@/pages/Quizzes";
import QuizPlayer from "@/pages/QuizPlayer";
import Typing from "@/pages/Typing";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import AIGenerator from "@/pages/AIGenerator";
import Versant from "@/pages/Versant";
import ARSimulator from "@/pages/ARSimulator";
import AMLSimulator from "@/pages/AMLSimulator";
import Bundles from "@/pages/Bundles";
import ContentEditor from "@/pages/ContentEditor";
import Notifications from "@/pages/Notifications";
import AdminTasks from "@/pages/AdminTasks";
import ImportCourse from "@/pages/ImportCourse";
import Subscribe from "@/pages/Subscribe";
import VoiceDrills from "@/pages/VoiceDrills";
import Members from "@/pages/Members";
import ContactInbox from "@/pages/ContactInbox";
import AppLayout from "@/components/AppLayout";
import { Loader2 } from "lucide-react";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" data-testid="app-loader" />
      </div>
    );
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.target_path && user.role === "student") return <Navigate to="/onboarding" replace />;
  if (user.role === "student" && !user.subscription_active) return <Navigate to="/subscribe" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<MarketingHome />} />
          <Route path="/services" element={<MarketingServices />} />
          <Route path="/success-stories" element={<MarketingSuccess />} />
          <Route path="/about" element={<MarketingAbout />} />
          <Route path="/contact" element={<MarketingContact />} />
          <Route path="/register" element={<MarketingRegister />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/quizzes" element={<Protected><Quizzes /></Protected>} />
          <Route path="/quizzes/:id" element={<Protected><QuizPlayer /></Protected>} />
          <Route path="/typing" element={<Protected><Typing /></Protected>} />
          <Route path="/versant" element={<Protected><Versant /></Protected>} />
          <Route path="/voice-drills" element={<Protected><VoiceDrills /></Protected>} />
          <Route path="/ar-sim" element={<Protected><ARSimulator /></Protected>} />
          <Route path="/aml-sim" element={<Protected><AMLSimulator /></Protected>} />
          <Route path="/bundles" element={<Protected><Bundles /></Protected>} />
          <Route path="/courses" element={<Protected><Courses /></Protected>} />
          <Route path="/courses/:id" element={<Protected><CourseDetail /></Protected>} />
          <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
          <Route path="/admin/generate" element={<Protected adminOnly><AIGenerator /></Protected>} />
          <Route path="/admin/editor" element={<Protected adminOnly><ContentEditor /></Protected>} />
          <Route path="/admin/import" element={<Protected adminOnly><ImportCourse /></Protected>} />
          <Route path="/admin/members" element={<Protected adminOnly><Members /></Protected>} />
          <Route path="/admin/inbox" element={<Protected adminOnly><ContactInbox /></Protected>} />
          <Route path="/admin/emails" element={<Protected adminOnly><Notifications /></Protected>} />
          <Route path="/admin/tasks" element={<Protected adminOnly><AdminTasks /></Protected>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
